import { useState, useEffect, useMemo } from 'react';
import API, { fmt } from '@/lib/api';
import { Warning, MagnifyingGlass, ArrowUp, ArrowDown, PaperPlaneRight } from '@phosphor-icons/react';
import BetalingsherinneringModal from '@/components/BetalingsherinneringModal';

const NIVEAU_INFO = {
  1: { label: '1e Herinnering', kleur: '#002FA7' },
  2: { label: '2e Herinnering', kleur: '#EA580C' },
  3: { label: 'Ingebrekestelling', kleur: '#B91C1C' },
};
const KANAAL_ICON = { pdf: '📄', email: '✉️', whatsapp: '💬' };

export default function BetalingsherinneringenOverzichtPage() {
  const [rows, setRows] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoek, setZoek] = useState('');
  const [niveauFilter, setNiveauFilter] = useState('alle');
  const [sortKey, setSortKey] = useState('totaal_openstaand');
  const [sortDir, setSortDir] = useState('desc');
  const [bhModal, setBhModal] = useState(null); // { klantId, niveau }

  const load = () => {
    setLoading(true);
    API.get('/betalingsherinneringen/overzicht').then(r => setRows(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { API.get('/klanten').then(r => setKlanten(r.data || [])).catch(() => {}); }, []);
  const kFull = (kid) => klanten.find(k => k.id === kid) || {};

  const filtered = useMemo(() => {
    let out = rows.filter(r => (r.klant_naam || '').toLowerCase().includes(zoek.toLowerCase()));
    if (niveauFilter === 'nooit') out = out.filter(r => !r.laatste_niveau);
    else if (niveauFilter !== 'alle') out = out.filter(r => r.laatste_niveau === parseInt(niveauFilter));
    out.sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, zoek, niveauFilter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortTh = ({ k, children }) => (
    <th className="cursor-pointer select-none hover:text-[#002FA7]" onClick={() => toggleSort(k)} data-testid={`bh-overzicht-sort-${k}`}>
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === k && (sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </span>
    </th>
  );

  const fmtDatum = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div data-testid="bh-overzicht-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="page-heading mb-0">Betalingsherinneringen — overzicht</h1>
        <span className="text-xs text-gray-500">{filtered.length} van {rows.length} klant(en) met openstaand saldo</span>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Zoek op klant..."
            value={zoek}
            onChange={e => setZoek(e.target.value)}
            className="field-input pl-8 text-sm"
            data-testid="bh-overzicht-zoek"
          />
        </div>
        <select value={niveauFilter} onChange={e => setNiveauFilter(e.target.value)} className="field-input text-sm w-auto" data-testid="bh-overzicht-niveau-filter">
          <option value="alle">Alle niveaus</option>
          <option value="nooit">Nog nooit verstuurd</option>
          <option value="1">Laatst: 1e Herinnering</option>
          <option value="2">Laatst: 2e Herinnering</option>
          <option value="3">Laatst: Ingebrekestelling</option>
        </select>
      </div>

      <div className="section-card">
        <div className="section-body">
          <table className="data-table">
            <thead>
              <tr>
                <SortTh k="klant_naam">Klant</SortTh>
                <SortTh k="aantal_facturen">Facturen</SortTh>
                <SortTh k="totaal_openstaand">Openstaand</SortTh>
                <SortTh k="oudste_vervaldag">Oudste vervaldag</SortTh>
                <th>Laatst verstuurd</th>
                <th>Volgende stap</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const niv = r.laatste_niveau ? NIVEAU_INFO[r.laatste_niveau] : null;
                const volgende = NIVEAU_INFO[r.volgende_niveau] || NIVEAU_INFO[1];
                return (
                  <tr key={r.klant_id} className="hover:bg-[#F0F4FF]" data-testid={`bh-overzicht-row-${r.klant_id}`}>
                    <td className="font-medium">{r.klant_naam}</td>
                    <td>{r.aantal_facturen}</td>
                    <td className="font-bold text-red-600">{fmt(r.totaal_openstaand)}</td>
                    <td>{fmtDatum(r.oudste_vervaldag)}</td>
                    <td>
                      {niv ? (
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span>{KANAAL_ICON[r.laatste_kanaal] || '📄'}</span>
                          <span className="font-bold" style={{ color: niv.kleur }}>{niv.label}</span>
                          <span className="text-gray-400">· {fmtDatum(r.laatste_datum)}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Nog niet verstuurd</span>
                      )}
                    </td>
                    <td>
                      <span className="text-[10px] font-bold px-2 py-0.5 uppercase" style={{ background: `${volgende.kleur}1A`, color: volgende.kleur }}>
                        {volgende.label}
                      </span>
                    </td>
                    <td className="text-right">
                      <button
                        className="btn btn-sm bg-[#B91C1C] hover:bg-[#991B1B] text-white inline-flex items-center gap-1.5"
                        onClick={() => setBhModal({ klantId: r.klant_id, niveau: r.volgende_niveau })}
                        data-testid={`bh-overzicht-versturen-${r.klant_id}`}
                      >
                        <PaperPlaneRight size={13} weight="bold" /> Versturen
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="7" className="text-center text-gray-400 py-8">
                  {rows.length === 0 ? <><Warning size={16} className="inline mr-1.5" />Geen klanten met openstaand saldo — alles betaald!</> : 'Geen resultaten voor deze filter'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {bhModal && (
        <BetalingsherinneringModal
          open={!!bhModal}
          onClose={() => { setBhModal(null); load(); }}
          klantId={bhModal.klantId}
          klantNaam={(rows.find(r => r.klant_id === bhModal.klantId) || {}).klant_naam || ''}
          klantEmail={kFull(bhModal.klantId).email || ''}
          klantGsm={kFull(bhModal.klantId).gsm || kFull(bhModal.klantId).tel || ''}
          klantTaal={kFull(bhModal.klantId).taal || 'nl'}
          defaultNiveau={bhModal.niveau}
        />
      )}
    </div>
  );
}
